import type { InteractionMessage, PublicRow, WireMessage } from './types';
import type { Welcome } from '../state';
import { isInteractionPresentation, isInteractionResponse } from './validators';

const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const statuses = new Set(['preparing', 'pending', 'accepted', 'resumed', 'cancelled', 'expired']);

export function readInteraction(value: unknown): (InteractionMessage & { role: 'assistant' }) | null {
  if (!object(value) || value.type !== 'interaction' || typeof value.id !== 'string' || !value.id ||
      value.role !== 'assistant' || typeof value.content !== 'string' ||
      typeof value.interaction_id !== 'string' || !value.interaction_id ||
      typeof value.tool_type !== 'string' || !Number.isSafeInteger(value.schema_version) || Number(value.schema_version) < 1 ||
      !Number.isSafeInteger(value.interaction_revision) || Number(value.interaction_revision) < 1 ||
      !statuses.has(String(value.interaction_status)) || !object(value.presentation)) return null;
  const pair = { tool_type: value.tool_type, schema_version: Number(value.schema_version) };
  const responsePayload = value.response_payload;
  if (responsePayload !== undefined && responsePayload !== null && !isInteractionResponse(pair, responsePayload)) return null;
  return {
    id: value.id, type: 'interaction', role: 'assistant', content: value.content,
    interaction_id: value.interaction_id, tool_type: value.tool_type, schema_version: Number(value.schema_version),
    presentation: value.presentation as unknown as InteractionMessage['presentation'],
    interaction_status: value.interaction_status as InteractionMessage['interaction_status'],
    interaction_revision: Number(value.interaction_revision),
    ...(typeof value.created_at === 'string' ? { created_at: value.created_at } : {}),
    ...(typeof value.selected_option_id === 'string' ? { selected_option_id: value.selected_option_id } : {}),
    ...(responsePayload !== undefined ? { response_payload: responsePayload as InteractionMessage['response_payload'] } : {}),
  };
}

export function readPublicMessage(value: unknown): PublicRow | null {
  if (!object(value) || (typeof value.id !== 'string' && typeof value.id !== 'number') ||
      typeof value.content !== 'string' || !['assistant', 'user', 'operator', 'system'].includes(String(value.role))) return null;
  const interaction = readInteraction({ ...value, id: String(value.id) });
  return {
    id: String(value.id), content: value.content,
    type: value.type === 'interaction' ? 'interaction' : 'message',
    ts: typeof value.created_at === 'string' ? value.created_at : typeof value.ts === 'string' ? value.ts : new Date().toISOString(),
    ...(interaction ?? {}),
    role: value.role as PublicRow['role'],
  };
}

export function interactionCursor(value: unknown): string | null {
  return typeof value === 'string' && /^\d+$/.test(value) ? value.replace(/^0+(?=\d)/, '') : null;
}

export function compareCursors(left: string, right: string): number {
  return left.length === right.length ? (left === right ? 0 : left > right ? 1 : -1) : left.length > right.length ? 1 : -1;
}

export function mergeItems<T extends { id: string; interaction_id?: string; interaction_revision?: number; interaction_status?: string }>(current: T[], additions: T[]): T[] {
  const result = [...current];
  for (const message of additions) {
    const index = result.findIndex((row) => row.id === message.id ||
      (message.interaction_id && row.interaction_id === message.interaction_id));
    if (index < 0) result.push(message);
    else {
      const previous = result[index]!;
      const lifecycleRank = (row: T) => row.interaction_status === 'preparing' ? 0 :
        row.interaction_status === 'pending' ? 1 : row.interaction_status === 'accepted' ? 2 : 3;
      const equalRevisionDowngrade = message.interaction_revision === previous.interaction_revision &&
        lifecycleRank(message) < lifecycleRank(previous);
      if (!equalRevisionDowngrade && (!message.interaction_revision || !previous.interaction_revision ||
        message.interaction_revision >= previous.interaction_revision)) result[index] = { ...previous, ...message };
    }
  }
  return result;
}

const WIRE_ROLES = new Set(['user', 'ai', 'assistant', 'operator', 'support', 'system']);

export function readWireMessage(v: unknown): WireMessage | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const id = typeof o.id === 'number' ? String(o.id) : o.id;
  if (typeof id !== 'string' || id === '') return null;
  if (typeof o.content !== 'string') return null;
  if (typeof o.role !== 'string' || !WIRE_ROLES.has(o.role)) return null;
  const out: WireMessage = { id, role: o.role as WireMessage['role'], content: o.content };
  if (typeof o.created_at === 'string') out.created_at = o.created_at;
  if (o.type === 'interaction' || o.type === 'message') out.type = o.type;
  return out;
}

/** The agent's welcome message. An invalid interaction is dropped; the text still shows. */
export function readWelcome(v: unknown): Welcome | null {
  if (!object(v) || typeof v.message !== 'string' || !v.message.trim() || [...v.message].length > 1000) return null;
  const out: Welcome = { message: v.message };
  const i = v.interaction;
  if (object(i) && (i.tool_type === 'ui_buttons' || i.tool_type === 'ui_carousel') && i.schema_version === 1 &&
      isInteractionPresentation({ tool_type: i.tool_type, schema_version: 1 }, i.presentation)) {
    out.interaction = { tool_type: i.tool_type, schema_version: 1, presentation: i.presentation };
  }
  return out;
}
